import { useState } from "react";
import { MessageCircle, Check, X, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cleanPhone } from "@/lib/format";
import { renderWhatsAppTemplate } from "@/lib/plans";

export default function BulkWhatsAppDialog({ open, onOpenChange, prospectos = [], template }) {
  const [opened, setOpened] = useState(() => new Set());

  const items = prospectos.map((p) => {
    const num = cleanPhone(p.contato);
    const msg = renderWhatsAppTemplate(template, p);
    const url = num ? `https://wa.me/${num}?text=${encodeURIComponent(msg)}` : null;
    return { ...p, num, msg, url };
  });
  const validos = items.filter((i) => i.url);

  const openOne = (id, url) => {
    window.open(url, "_blank", "noopener,noreferrer");
    setOpened((prev) => { const n = new Set(prev); n.add(id); return n; });
  };

  const copyMsg = async (msg) => {
    try {
      await navigator.clipboard.writeText(msg);
      toast.success("Mensagem copiada");
    } catch { toast.error("Não foi possível copiar"); }
  };

  const resetAndClose = () => {
    setOpened(new Set());
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-bulk-whatsapp">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-500" /> Reengajamento em lote
          </DialogTitle>
        </DialogHeader>

        <div className="text-xs text-muted-foreground mb-3">
          Clique em <span className="font-semibold">Abrir</span> para disparar o WhatsApp de cada prospecto (o navegador bloqueia disparo em massa automático).
          {items.length - validos.length > 0 && (
            <span className="ml-1 text-amber-500">
              · {items.length - validos.length} sem telefone válido serão pulados
            </span>
          )}
        </div>

        <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1 thin-scroll">
          {items.map((i) => {
            const ok = opened.has(i.id);
            return (
              <div
                key={i.id}
                className={`rounded-lg border p-3 flex items-start gap-3 ${ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-border"}`}
                data-testid={`bulk-row-${i.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-sm truncate">{i.nome}</div>
                    <Badge variant="outline" className="text-[10px]">{i.vendedor}</Badge>
                    {i.num ? (
                      <span className="text-[11px] text-muted-foreground font-mono">{i.contato}</span>
                    ) : (
                      <Badge variant="outline" className="text-[10px] bg-red-500/15 text-red-400 border-red-500/30">
                        Sem telefone
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2">{i.msg}</p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  {i.url && (
                    <Button
                      size="sm"
                      variant={ok ? "outline" : "default"}
                      className={ok ? "border-emerald-500/40 text-emerald-500" : ""}
                      onClick={() => openOne(i.id, i.url)}
                      data-testid={`btn-open-bulk-${i.id}`}
                    >
                      {ok ? <Check className="w-3.5 h-3.5 mr-1" /> : <MessageCircle className="w-3.5 h-3.5 mr-1" />}
                      {ok ? "Aberto" : "Abrir"}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => copyMsg(i.msg)} data-testid={`btn-copy-bulk-${i.id}`}>
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <div className="text-xs text-muted-foreground tabular-nums">
              {opened.size}/{validos.length} abertos
            </div>
            <Button variant="outline" onClick={resetAndClose}>
              <X className="w-4 h-4 mr-1.5" /> Fechar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
